import {Component, OnInit, ViewChild} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {AuthService} from '../../services/auth/auth.service';
import {NotificationService} from '../../services/common/notification.service';
import {FormOptionsLists} from '../../shared/FormOptionsLists';
import {SearchTriggerObject} from '../../shared/SearchTrigger';
import {CategoryTreeComponent, EventData} from '../common/category-tree/category-tree.component';
import {TableComponent} from '../common/table/table.component';
import {VerbalActionEntryService} from '../../services/verbal-action/verbal-action-entry.service';
import {VerbalActionCategoryService} from '../../services/verbal-action/verbal-action-category.service';
import {NewVerbalActionComponent} from './create/new-verbal-action/new-verbal-action.component';
import {NewVerbalActionCategoryComponent} from './create/new-category/new-verbal-action-category.component';
import {VerbalActionEntryDto} from '../../dtos/verbal-action/entry/verbal-action-entry-dto';
import {ActivatedRoute, Router} from '@angular/router';
import {filter} from 'rxjs';
import {SharedService} from '../../services/common/shared.service';

@Component({
  selector: 'app-verbal-action',
  templateUrl: './verbal-action.component.html',
  styleUrls: ['./verbal-action.component.scss'],
  standalone: false
})
export class VerbalActionComponent implements OnInit {

  @ViewChild(TableComponent) tableC: TableComponent;
  @ViewChild(CategoryTreeComponent) treeC: CategoryTreeComponent;

  displayedColumns: string[];
  formOptionsLists = new FormOptionsLists();

  categoryControl: number = null;
  reloadRootsTrigger = false;
  searchTrigger: SearchTriggerObject = {trigger: false, params: new HttpParams()};
  resetCategoryControl = false;
  resetSearchTrigger = false;

  isAuthorized: boolean;
  isAdmin: boolean;
  displayEditingTools = false;

  // checkboxes to add entries to a Sammelmappe, off until the user asks for them
  selectMode = false;

  toggleSelectMode(): void {
    this.selectMode = !this.selectMode;
  }

  // for references / global search deep links
  entryId: number = null;
  categoryId: number = null;
  initialSearchQuery: string = null;

  constructor(
    protected verbalActionEntryService: VerbalActionEntryService,
    protected verbalActionCategoryService: VerbalActionCategoryService,
    private notificationService: NotificationService,
    public authService: AuthService,
    public matDialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService
  ) {}

  ngOnInit() {
    [this.isAuthorized, this.isAdmin] = this.authService.isAuthorizedAndAdmin();
    this.reloadRootsTrigger = !this.reloadRootsTrigger;
    this.displayedColumns = this.isAdmin && this.displayEditingTools
      ? this.formOptionsLists.displayedColumnsVAEditing
      : this.formOptionsLists.displayedColumnsVADetails;

    this.route.queryParams
      .pipe(filter(params => !!params['id'] || !!params['c_id'] || !!params['q']))
      .subscribe((params: HttpParams) => {
        if (params['q']) {
          this.initialSearchQuery = params['q'];
          // clear any previously selected category so it doesn't narrow this deep-linked search
          this.resetCategoryControl = !this.resetCategoryControl;
          this.categoryControl = null;
        }
        this.entryId = params['id'];
        if (this.entryId) {
          // clear any previously selected category so it doesn't hide this deep-linked entry
          this.resetCategoryControl = !this.resetCategoryControl;
          this.categoryControl = null;
          let httpParams = new HttpParams();
          httpParams = httpParams.set('id', this.entryId);
          this.handleSearch(httpParams);
        }
        // category tree is all handled in the tree component
        this.categoryId = params['c_id'];
        if (this.categoryId) {
          // clear the rest of the filter so it doesn't combine with the deep-linked category
          this.resetSearchTrigger = !this.resetSearchTrigger;
          if (this.tableC) {
            this.tableC.sort.active = 'created';
            this.tableC.paginator.pageSize = 25;
            this.tableC.paginator.pageIndex = 0;
          }
        }
        this.sharedService.clearIdParam(this.router);
      });
  }

  toggleEditingTools(change: boolean) {
    if (change) {
      this.displayEditingTools = !this.displayEditingTools;
    }
    if (this.displayEditingTools && this.isAdmin) {
      if (!this.displayedColumns.includes('edit')) {
        const lastIndex = this.displayedColumns.length - 1;
        this.displayedColumns.splice(lastIndex, 0, 'edit');
      }
    } else {
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'edit');
    }
  }

  openNewCategoryDialog() {
    const dialogRef = this.matDialog.open(NewVerbalActionCategoryComponent);
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.reloadRootsTrigger = !this.reloadRootsTrigger;
        }
      },
      error: (error) => this.notificationService.openSnackBar(error)
    });
  }

  openNewVerbalActionDialog() {
    const dialogRef = this.matDialog.open(NewVerbalActionComponent);
    dialogRef.afterClosed().subscribe({
      next: (result: VerbalActionEntryDto) => {
        if (result && result.id) {
          void this.router.navigateByUrl('/sprachhandlungen/' + result.id);
        }
      },
      error: (error) => {
        this.notificationService.openSnackBar(error);
      }
    });
  }

  handleChildSearch(data: EventData) {
    this.categoryControl = data.categoryControl;
    if (data.resetPageIndex && this.tableC) {
      this.tableC.paginator.pageIndex = 0;
    }
    // clear any previously searched entry
    this.entryId = null;
    this.resetSearchTrigger = !this.resetSearchTrigger;
    this.searchTrigger.trigger = !this.searchTrigger.trigger;
  }

  handleSearch(params: HttpParams) {
    this.searchTrigger.params = params;
  }

  handleResetFilter() {
    this.tableC.sort.active = 'created';
    this.tableC.paginator.pageSize = 25;
    this.tableC.paginator.pageIndex = 0;
    this.resetCategoryControl = !this.resetCategoryControl;
    this.categoryControl = null;
  }
}
